import React, { ChangeEvent, createRef, useEffect, useState } from "react";
import classnames from "classnames";
import { validate } from "email-validator";

import getRegionComplianceRules from "api/requests/consumerApi/getRegionComplianceRules";
import { getDispensary } from "api/requests/getDispensary";
import consumerApi from "api/services/consumer-api";
import { DISPENSARY_PAGES } from "constants/dispensary";
import { Action, Category, Label } from "constants/events";
import { CountryCode } from "custom-types/CountryCode";
import { Dispensary } from "custom-types/Dispensary";
import { Themes } from "custom-types/Themes";
import { doctorsMetadata } from "lib/dispensaryMetadata";
import { useDispatch } from "redux/hooks";
import {
  COMPLIANCE_RULESET_LOAD,
  ComplianceRuleset,
} from "redux/reducers/complianceRules";
import { createGetServerSideProps } from "utils/createGetServerSideProps";
import getDispensaryPrimaryAddress from "utils/dispensary/getDispensaryPrimaryAddress";
import generateDispensaryRedirect from "utils/generateDispensaryRedirect";
import logError from "utils/logError";
import { getCountryCodeHeader } from "utils/requestContext";
import { trackEvent } from "utils/trackEvent";

import Button from "components/botanic/Button";
import Input from "components/botanic/Input";
import Select from "components/botanic/Select";
import DispensaryHeader from "components/Dispensary/DispensaryHeader";
import DispensaryTabs from "components/Dispensary/DispensaryTabs";
import DispensaryMap from "components/Dispensary/Map";
import MetaTags from "components/MetaTags";
import Option from "components/Option";

const TIMES = [
  "As soon as possible",
  "This Week",
  "Next Week",
  "This Month",
  "Next Month",
];

type AppointmentPageProps = {
  complianceRuleset: ComplianceRuleset;
  dispensary: Dispensary;
};

const AppointmentPage: React.FC<AppointmentPageProps> = ({
  complianceRuleset,
  dispensary,
}) => {
  const dispatch = useDispatch();

  const form = createRef<HTMLFormElement>();

  const [
    disableButtonOnSuccessfulSubmission,
    setdisableButtonOnSuccessfulSubmission,
  ] = useState(false);
  const [emailFieldCompleted, setemailFieldCompleted] = useState(false);
  const [isEmailInvalid, setisEmailInvalid] = useState(false);
  const [isFirstNameInvalid, setisFirstNameInvalid] = useState(false);
  const [isLastNameInvalid, setisLastNameInvalid] = useState(false);
  const [isPhoneInvalid, setisPhoneInvalid] = useState(false);
  const [firstNameFieldCompleted, setFirstNameFieldCompleted] = useState(false);
  const [lastNameFieldCompleted, setLastNameFieldCompleted] = useState(false);
  const [phoneFieldCompleted, setphoneFieldCompleted] = useState(false);
  const [showSubmissionStatus, setshowSubmissionStatus] = useState(false);
  const [submissionIsSuccessful, setsubmissionIsSuccessful] = useState(false);
  const [timeFieldCompleted, settimeFieldCompleted] = useState(false);

  useEffect(() => {
    if (complianceRuleset) {
      dispatch({
        complianceRuleset,
        type: COMPLIANCE_RULESET_LOAD,
      });
    }
  }, [dispatch]);

  const validateEmail = (event: ChangeEvent<HTMLInputElement>) => {
    const patternMismatch = !validate(event.target.value);
    setisEmailInvalid(patternMismatch);
    setemailFieldCompleted(!patternMismatch);
  };

  const validatePhone = (event: ChangeEvent<HTMLInputElement>) => {
    const { patternMismatch } = event.target.validity;
    setisPhoneInvalid(patternMismatch);
    setphoneFieldCompleted(!patternMismatch);
  };

  const validateFirstName = (event: ChangeEvent<HTMLInputElement>) => {
    setdisableButtonOnSuccessfulSubmission(false);
    const { valueMissing } = event.target.validity;
    setisFirstNameInvalid(valueMissing);
    setFirstNameFieldCompleted(!valueMissing);
  };

  const validateLastName = (event: ChangeEvent<HTMLInputElement>) => {
    const { valueMissing } = event.target.validity;
    setisLastNameInvalid(valueMissing);
    setLastNameFieldCompleted(!valueMissing);
  };

  const handleSubmit = (event: ChangeEvent<HTMLFormElement>) => {
    event.preventDefault();
    const formData = new FormData(event.target);
    submitFormData(formData);
  };

  const onSuccessfulSubmission = () => {
    setemailFieldCompleted(false);
    setFirstNameFieldCompleted(false);
    setLastNameFieldCompleted(false);
    setphoneFieldCompleted(false);
    setshowSubmissionStatus(true);
    setsubmissionIsSuccessful(true);
  };

  const submitFormData = (formData: FormData) => {
    setdisableButtonOnSuccessfulSubmission(true);
    // eslint-disable-next-line @typescript-eslint/no-explicit-any -- TODO: fix me please, do not replicate
    let appointmentTime: any;
    const formValues = {};
    for (const formField of formData) {
      // @ts-ignore (fix me please, do not replicate)
      if (formField === "schedule_for") {
        appointmentTime = formField[1];
      }
      // @ts-ignore (fix me please, do not replicate)
      formValues[formField[0]] = formField[1];
    }

    const { first_name, last_name, email, phone, schedule_for } =
      // eslint-disable-next-line @typescript-eslint/no-explicit-any -- TODO: fix me please, do not replicate
      formValues as any;

    const fullName = `${first_name} ${last_name}`;

    consumerApi
      .post(
        `api/dispensaries/v1/${dispensary.id}/doctor_appointment_request`,
        {
          patient_email: email,
          patient_name: fullName,
          patient_phone: phone,
          schedule_for,
        },
        {
          headers: { "Content-Type": "application/json" },
        },
      )
      .then(() => {
        trackEvent(
          Category.doctorProfile,
          Action.click,
          Label.submitAppointment,
          {
            appointmentTime,
            dispensaryId: dispensary.id,
          },
        );

        onSuccessfulSubmission();
      })
      .catch((e) => {
        logError(e.message, {
          functionName: "appointment submitFormData",
          service: "consumer",
          statusCode: e.statusCode,
        });

        setshowSubmissionStatus(true);
        setsubmissionIsSuccessful(false);
      });
  };

  const primaryLocation = getDispensaryPrimaryAddress(dispensary);

  const isSubmitDisabled = !(
    firstNameFieldCompleted &&
    lastNameFieldCompleted &&
    emailFieldCompleted &&
    phoneFieldCompleted &&
    timeFieldCompleted &&
    !disableButtonOnSuccessfulSubmission &&
    dispensary.email
  );

  const { coverPhotoUrl, logoUrl, name } = dispensary;
  const { description, image, title } = doctorsMetadata(
    coverPhotoUrl,
    logoUrl,
    name,
  );

  return (
    <>
      <MetaTags description={description} image={image} title={title} />
      <DispensaryHeader dispensary={dispensary} />
      <DispensaryTabs
        activeTab={DISPENSARY_PAGES.appointment}
        dispensary={dispensary}
      />
      <div className="container flex flex-col md:flex-row mt-lg gap-4 md:gap-11">
        <div className="w-full">
          {!dispensary.email && (
            <div className="bg-yellow-20 border border-yellow-40 p-lg font-bold text-center">
              Appointment request not available
            </div>
          )}
          <form onSubmit={handleSubmit} ref={form} autoComplete="off">
            <div className="form-field mb-md relative">
              <Input
                label="First name"
                id="first_name"
                name="first_name"
                onChange={validateFirstName}
                required
                theme={Themes.light}
              />
              {isFirstNameInvalid && (
                <p className="text-error text-xs">First name is required</p>
              )}
            </div>
            <div className="form-field my-xl relative">
              <Input
                label="Last name"
                id="last_name"
                name="last_name"
                onChange={validateLastName}
                required
                theme={Themes.light}
              />
              {isLastNameInvalid && (
                <p className="text-error text-xs">Last name is required</p>
              )}
            </div>
            <div
              className={classnames("form-field my-xl relative", {
                "text-error": isEmailInvalid,
              })}
            >
              <Input
                label="Email"
                id="_email"
                name="email"
                onChange={validateEmail}
                required
                theme={Themes.light}
              />
              {isEmailInvalid && (
                <p className="text-error text-xs">Valid email is required</p>
              )}
            </div>
            <div
              className={classnames("form-field my-xl relative", {
                "text-error": isPhoneInvalid,
              })}
            >
              <Input
                label="Phone number"
                type="tel"
                id="phone"
                name="phone"
                pattern="[0-9]{3}[0-9]{3}[0-9]{4}"
                onChange={validatePhone}
                required
                theme={Themes.light}
              />
              {isPhoneInvalid && (
                <p className="text-error text-xs">Valid phone is required</p>
              )}
            </div>
            <div>
              <Select
                name="schedule_for"
                id="select-time"
                theme={Themes.light}
                defaultValue="Choose time"
                className="mb-xl"
                onChange={(event) => {
                  settimeFieldCompleted(event.target.value !== "Choose time");
                }}
              >
                <Option value="Choose time">Choose time</Option>
                {TIMES.map((time) => (
                  <Option value={time} key={time}>
                    {time}
                  </Option>
                ))}
              </Select>
            </div>
            <Button
              className={classnames({
                "bg-light-grey text-default": isSubmitDisabled,
              })}
              disabled={isSubmitDisabled}
              size="full"
              type="submit"
            >
              {isSubmitDisabled ? "complete form to submit" : "submit"}
            </Button>
          </form>
          <div className="min-h-[110px] md:mb-xxl mt-lg">
            {!submissionIsSuccessful && showSubmissionStatus && (
              <div className="bg-yellow-20 border border-yellow-40 p-lg font-bold text-center">
                There was a problem submitting. Try again.
              </div>
            )}
            {submissionIsSuccessful && showSubmissionStatus && (
              <div className="bg-green-20 border border-green-40 p-lg font-bold text-center">
                <p className="mb-1">Success!</p>
                <p className="mb-0 font-normal">{`${dispensary.name} will contact you to schedule`}</p>
              </div>
            )}
          </div>
        </div>
        <div className="w-full mb-xxl md:mb-0">
          <div className="text-sm mb-md">{primaryLocation}</div>
          <div className="aspect-square">
            <DispensaryMap location={dispensary.primaryLocation} />
          </div>
        </div>
      </div>
    </>
  );
};

export const getServerSideProps =
  createGetServerSideProps<AppointmentPageProps>(async (context) => {
    const { req, query, resolvedUrl } = context;
    const { slug } = query;

    const dispensary = await getDispensary(slug as string);
    const domainCountryCode = getCountryCodeHeader(req.headers) as CountryCode;

    const redirect = generateDispensaryRedirect(
      domainCountryCode,
      resolvedUrl,
      query,
      dispensary,
    );

    if (redirect) {
      return { redirect };
    }

    // fallback if redirect not generated
    if (!dispensary) {
      return {
        notFound: true,
      };
    }

    const complianceRuleset = await getRegionComplianceRules(
      dispensary.state,
      dispensary.country,
    );

    return {
      props: {
        complianceRuleset,
        dispensary,
      },
    };
  });

export default AppointmentPage;
